import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { StyledH5 } from "@components/Styled/Text";
import React, { useMemo } from "react";
import tw from "twin.macro";
import {
  FindInStorePopupOutputProps,
  withFindInStorePopup,
} from "./withFindInStorePopup";
import { StoreAvailability } from "@shopify/hydrogen-react/storefront-api-types";

type StockLevelsLabelProps = {
  availability: StoreAvailability;
  lowStockThreshold: number;
};

const Title = tw(StyledH5)``;

const Wrapper = tw.div`
  relative bg-white p-midi w-full z-50
`;
const StyledIcon = tw(Icon)`
  absolute top-mini right-mini
`;
const Result = tw.div`
  py-midi border-b border-quaternary last:border-none
`;
const AddressWrapper = tw.div`
  flex flex-row justify-between
`;
const Address = tw.p``;
const StoreName = tw.h4`
  font-medium text-h4
`;

const StockLevels = tw.p`
  w-full
`;
const Phone = withFocusRing(tw.a`text-body border-b`);
const Close = withFocusRing(tw.button``);

const Stock = ({ availability, lowStockThreshold }: StockLevelsLabelProps) => {
  if (availability.quantityAvailable <= 0) {
    return <p className="font-medium text-primary">Out of Stock</p>;
  }

  if (availability.quantityAvailable < lowStockThreshold) {
    return <p className="font-medium text-warning">Low stock</p>;
  }

  return <p className="font-medium text-success">In Stock</p>;
};

const FindInStorePopup = withFindInStorePopup(
  ({
    setActive,
    className,
    selectedVariant,
    stockLevelsLabel,
    lowStockThreshold,
    countryCode,
  }: FindInStorePopupOutputProps) => {
    const storeAvailability = useMemo(() => {
      const baseCountry = "AU";
      return {
        ByUserCountry:
          selectedVariant?.storeAvailability?.filter(
            (availability) =>
              availability?.location?.address?.countryCode === countryCode &&
              availability?.location?.address?.countryCode !== baseCountry &&
              availability?.location?.name !== "Oakleigh",
          ) ?? [],
        ByBaseCountry:
          selectedVariant?.storeAvailability?.filter(
            (availability) =>
              availability?.location?.address?.countryCode === baseCountry &&
              availability?.location?.name !== "Oakleigh",
          ) ?? [],
        ByROW:
          selectedVariant?.storeAvailability?.filter(
            (availability) =>
              availability?.location?.address?.countryCode !== countryCode &&
              availability?.location?.address?.countryCode !== baseCountry &&
              availability?.location?.name !== "Oakleigh",
          ) ?? [],
      };
    }, [selectedVariant]);

    return (
      <div className={className}>
        <Wrapper>
          <Close onClick={() => setActive(false)}>
            <StyledIcon name="close" size="xsmall" />
          </Close>
          <Title>Find in-store</Title>
          <StockLevels>{stockLevelsLabel}</StockLevels>
          {[
            ...storeAvailability.ByUserCountry,
            ...storeAvailability.ByBaseCountry,
            ...storeAvailability.ByROW,
          ].map((availability) => {
            return (
              <Result
                key={availability.location.id}
                data-testid="store-information"
              >
                <AddressWrapper>
                  <StoreName>{availability.location.name}</StoreName>
                  <Stock
                    availability={availability}
                    lowStockThreshold={lowStockThreshold}
                  />
                </AddressWrapper>
                <Address>
                  {availability.location.address.address1}{" "}
                  {availability.location.address.address2}
                </Address>
                <Address>{`${availability.location.address.city}, ${availability.location.address.provinceCode} ${availability.location.address.zip}`}</Address>
                <AddressWrapper>
                  <Phone
                    href={`tel:${availability.location.address.phone}`}
                  >{`P. ${availability.location.address.phone}`}</Phone>
                </AddressWrapper>
              </Result>
            );
          })}
        </Wrapper>
      </div>
    );
  },
);

export default FindInStorePopup;
